import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import createTheme from "@mui/material/styles/createTheme";
import { composeWrappers } from "@redotech/react-util/component";
import { StyleRoot } from "@redotech/react-util/style";
import { signupFormSchema } from "@redotech/redo-model/signup-form-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { RedoClientProvider } from "@redotech/redo-web/client";
import {
  DEFAULT_REDO_OVERLAY_Z_INDEX,
  MODAL_REDO_OVERLAY_Z_INDEX,
  POPPER_REDO_OVERLAY_Z_INDEX,
} from "@redotech/redo-web/shopify/shopify-z-index";
import { isLoggedInToStorefront } from "@redotech/redo-web/utils/storefront-is-logged-in";
import { ShopifyAjaxClient } from "@redotech/shopify-client/ajax";
import { memo, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ShopifyExtensionClientProvider } from "../client/client-context";
import { RedoShopifyRpcClientContextProvider } from "../client/redo-shopify-rpc-client";
import { ExtensionShopifyClientContext } from "../shopify";
import STYLE_MANAGER from "../style-manager";
import { SignupFormInstanceWrapper } from "./marketing/signup-form/signup-form-instance-wrapper";
import { ReviewSurvey } from "./reviews/reviews-survey";
import { SaveForLaterSlideInPanel } from "./save-for-later/save-for-later-slide-in-panel";
import "./styles.css";
import "./styles.global.css";

// also referenced in "./styles.global.css"
export const REDO_STOREFRONT_OVERLAY_CONTAINER = "redo-storefront-container";

const RedoStorefrontOverlay = memo(function RedoStorefrontOverlay({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}) {
  const queryParameters = new URLSearchParams(location.search);
  const testForm = queryParameters.get("redo-signup-test");
  const forceId = queryParameters.get("signup-form-id") || undefined;
  const forceForm = queryParameters.get("signup-form-object") || undefined;

  const utmParamPrefix = "_shopify_sa_p=";
  const cookies = document.cookie.split(";");
  const utmParamsCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(utmParamPrefix),
  );
  const utmParams = utmParamsCookie
    ? decodeURIComponent(utmParamsCookie.substring(utmParamPrefix.length + 1))
    : "";

  const shouldShowSaveForLaterSlideInPanel =
    autocheck.customerAccounts.enabled &&
    autocheck.customerAccounts.saveForLaterPanel?.enabled &&
    !isLoggedInToStorefront();

  const forcedFormParseResult = forceForm
    ? signupFormSchema.safeParse(JSON.parse(forceForm))
    : undefined;
  if (forcedFormParseResult && !forcedFormParseResult?.success) {
    console.error("Failed to parse forced form", forcedFormParseResult.error);
  }
  const parsedForcedForm = forcedFormParseResult?.success
    ? forcedFormParseResult.data
    : undefined;

  return composeWrappers(
    (inner) => (
      <RedoClientProvider
        url={
          globalThis.redoStorefront?.env?.REDO_API_URL ??
          process.env.REDO_API_URL!
        }
      >
        {inner}
      </RedoClientProvider>
    ),
    (inner) => (
      <ShopifyExtensionClientProvider
        storeUrl={
          globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop
        }
        widgetId={globalThis.redoWidgetId}
      >
        {inner}
      </ShopifyExtensionClientProvider>
    ),
    (inner) => (
      <ExtensionShopifyClientContext.Provider
        value={ShopifyAjaxClient.fromEnv()}
      >
        {inner}
      </ExtensionShopifyClientContext.Provider>
    ),
    (inner) => (
      <RedoShopifyRpcClientContextProvider
        baseUrl={
          globalThis.redoStorefront?.env?.REDO_API_URL ??
          process.env.REDO_API_URL!
        }
        storeUrl={
          globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop
        }
        widgetId={globalThis.redoWidgetId}
      >
        {inner}
      </RedoShopifyRpcClientContextProvider>
    ),
  )(
    <>
      {shouldShowSaveForLaterSlideInPanel && (
        <SaveForLaterSlideInPanel
          saveForLaterMarketingEnabled={
            (autocheck.marketing?.enabled &&
              autocheck.marketing?.subscribeFromSaveForLater) ??
            false
          }
          saveForLaterPanelSettings={
            autocheck.customerAccounts.saveForLaterPanel
          }
        />
      )}
      {(autocheck.marketing?.enabled || testForm) && (
        <SignupFormInstanceWrapper
          brandKit={autocheck.brandKit}
          forceForm={parsedForcedForm ?? undefined}
          forceId={forceId}
          utmParams={utmParams}
        />
      )}
      {autocheck.reviews.enabled && <ReviewSurvey />}
    </>,
  );
});

export function initialize(autocheck: WidgetAutocheck) {
  if (globalThis.blockRedo) {
    return;
  }

  if (!document.getElementById(REDO_STOREFRONT_OVERLAY_CONTAINER)) {
    const redoContainer = document.createElement("div");
    redoContainer.id = REDO_STOREFRONT_OVERLAY_CONTAINER;
    redoContainer.style.zIndex = `${DEFAULT_REDO_OVERLAY_Z_INDEX}`;
    document.body.append(redoContainer);

    const root = createRoot(redoContainer);

    root.render(
      <StyleRoot container={redoContainer} styleManager={STYLE_MANAGER}>
        <MuiRoot container={redoContainer}>
          <RedoStorefrontOverlay autocheck={autocheck} />
        </MuiRoot>
      </StyleRoot>,
    );
  }
}

export function MuiRoot({
  container,
  children,
}: {
  container: Element;
  children: ReactNode;
}) {
  const shadowRoot = container.shadowRoot;

  if (!shadowRoot) {
    return null;
  }

  const muiRoot = document.createElement("div");
  shadowRoot.append(muiRoot);

  muiRoot.style.setProperty(
    "--popper-z-index",
    `${POPPER_REDO_OVERLAY_Z_INDEX}`,
  );

  const muiTheme = createTheme({
    components: {
      MuiModal: {
        defaultProps: {
          container: muiRoot,
          style: {
            position: "fixed",
            inset: "0",
            zIndex: MODAL_REDO_OVERLAY_Z_INDEX,
          },
        },
      },
      MuiBackdrop: {
        defaultProps: { style: { position: "fixed", inset: "0", zIndex: -1 } },
      },
      MuiPopper: {
        defaultProps: { container: muiRoot },
        styleOverrides: { root: { zIndex: POPPER_REDO_OVERLAY_Z_INDEX } },
      },
    },
  });

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}
